import { Box, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  CancelablePromise,
  CustomTerminologiesService,
  CustomTerminology,
} from 'api-clients/monolith';
import { useApiService } from 'hooks';
import React, { useCallback, useEffect, useRef, useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import CustomDropzone from 'components/CustomDropzone';
import {
  makeIsCustomTerminologiesEnabled,
  makeSelectWhoami,
} from 'containers/Auth_old/selectors';
import { addMessageAction } from 'containers/FlashMessage/actions';
import { useApiServiceMutation } from 'hooks/useApiServiceMutation';
import { usePostHog } from 'hooks/usePostHog';

import CustomTerminologyFilePreview from './CustomTerminologyFilePreview';
import { messages } from './messages';
import { CustomTerminologyObject } from './type';
import { coerceTerminologyFileType, getMessageFromResult } from './util';

// ********************************************************************************
// == Type ========================================================================
type PostFunction = (
  accountExternalId: string,
  requestBody: string,
) => CancelablePromise<CustomTerminologyObject>;

// == Constant ====================================================================
const MAX_FILE_SIZE = 10 * 1024 * 1024; /* bytes */

const useStyles = makeStyles(theme => ({
  disabledMessage: {
    color: theme.palette.common.gray700,
    marginTop: theme.spacing(3),
  },
  downloadSampleFileInstruction: {
    color: theme.palette.common.blue400,
    cursor: 'pointer',
  },
  loadingContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
  },
  instruction: {
    color: theme.palette.common.black,
  },
  sampleFileContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
  },
  section: {
    overflow: 'hidden',
    position: 'relative',
  },
  title: {
    color: theme.palette.common.gray800,
    fontWeight: 500,
    lineHeight: '24px',
    marginTop: theme.spacing(3),
  },
  uploadFileInstruction: {
    color: theme.palette.common.gray800,
  },
}));

// == Util ========================================================================
const handleDownloadSampleFile = () => {
  const content =
    `en,fr,es,de,pt\nHello,Bonjour,Hola,Hallo,Olá\nGoodbye,Au revoir,Adiós,Auf Wiedersehen,Até logo\nThank You,Merci,Gracias,Danke,Obrigado\nYes,Oui,Sí,Ja,Sim\nNo,Non,No,Nein,Não`.trim();

  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', 'custom_terminology.csv');

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// == Component ===================================================================
const CustomTerminologies: VFC = () => {
  const classes = useStyles();
  const customTerminologiesEnabled = useSelector(
    makeIsCustomTerminologiesEnabled(),
  );
  const dispatch = useDispatch();
  const intl = useIntl();
  const posthog = usePostHog();
  const whoami = useSelector(makeSelectWhoami());

  const removeFileRef = useRef<(() => void) | null>(null);

  // -- Callback ------------------------------------------------------------------
  const fetchCustomTerminologies = useCallback(
    () =>
      CustomTerminologiesService.getInternalApiCustomTerminologiesTerminologies(
        whoami.account_id,
      ),
    [whoami.account_id],
  );

  // -- State ---------------------------------------------------------------------
  const [activeTerminology, setActiveTerminology] =
    useState<CustomTerminology | null>(null);
  const { refetch, result: fetchResult } = useApiService(
    fetchCustomTerminologies,
  );

  // -- Mutation ------------------------------------------------------------------
  const { mutation: uploadCustomTerminology } = useApiServiceMutation<
    CustomTerminologyObject,
    PostFunction,
    void
  >(
    (accountExternalId: string, requestBody: string) =>
      CustomTerminologiesService.postInternalApiCustomTerminologiesTerminologies(
        accountExternalId,
        requestBody,
      ),
    {
      onError: result => {
        dispatch(addMessageAction(getMessageFromResult(result, intl), 'error'));
        if (!removeFileRef.current) {
          return;
        }

        removeFileRef.current();
        removeFileRef.current = null;
      },

      onSuccess: () => {
        dispatch(
          addMessageAction(
            intl.formatMessage(messages.processSuccess),
            'success',
          ),
        );
        refetch();
      },
    },
  );

  // -- Effect --------------------------------------------------------------------
  useEffect(() => {
    posthog.capture('custom_terminology:settings_view', {
      account_id: whoami.account_id,
      user_id: whoami.external_id,
    });
  }, [posthog, whoami.account_id, whoami.external_id]);

  useEffect(() => {
    if (fetchResult.status !== 'ready') {
      return;
    }

    const activeTerminology = fetchResult.data.custom_terminologies?.find(
      terminology => terminology.active,
    );
    if (!activeTerminology) {
      return;
    }

    setActiveTerminology(activeTerminology);
  }, [fetchResult]);

  // -- Handler -------------------------------------------------------------------
  const handleUpload = (file: File, removeFileCallback?: () => void) => {
    posthog.capture('custom_terminology:upload_click', {
      account_id: whoami.account_id,
      user_id: whoami.external_id,
    });

    if (removeFileCallback) {
      removeFileRef.current = removeFileCallback;
    }

    uploadCustomTerminology(whoami.account_id, coerceTerminologyFileType(file));
  };

  // -- UI ------------------------------------------------------------------------
  const instructionMessages = [
    messages.uploadInstruction,
    messages.languageCodesInstruction,
    messages.rowStructureInstruction,
    messages.maxTermLengthInstruction,
    messages.maxFileSizeInstruction,
  ];

  const LoadingContainer = () => (
    <Box
      className={classes.loadingContainer}
      alignItems="center"
      display="flex"
      justifyContent="center"
    >
      <CircularProgress />
    </Box>
  );

  const renderContent = () => {
    switch (fetchResult.status) {
      case 'error': {
        return (
          <Typography className={classes.instruction} variant="body1">
            <FormattedMessage {...messages.errorLoadingTerminology} />
          </Typography>
        );
      }

      case 'loading':
      case 'reloading': {
        return <LoadingContainer />;
      }

      case 'ready': {
        if (activeTerminology) {
          return (
            <CustomTerminologyFilePreview
              accountExternalId={whoami.account_id}
              setActiveTerminology={setActiveTerminology}
              terminology={activeTerminology}
            />
          );
        } /* else -- Account has no active Terminology */

        return (
          <CustomDropzone
            fileType=".csv"
            maxFiles={1}
            maxSize={MAX_FILE_SIZE}
            multiple={false}
            onChangeStatus={handleUpload}
            PreviewComponent={LoadingContainer}
          />
        );
      }

      default: {
        return null /* unexpected status */;
      }
    }
  };

  if (!customTerminologiesEnabled) {
    return (
      <section className={classes.section}>
        <Typography
          className={classes.disabledMessage}
          data-testid="custom-terminology-disabled"
          variant="body1"
        >
          <FormattedMessage {...messages.customTerminologyDisabled} />
        </Typography>
      </section>
    );
  } /* else -- enabled */

  return (
    <section className={classes.section}>
      <div>
        <ul>
          {instructionMessages.map(message => (
            <li key={message.id}>
              <Typography className={classes.instruction} variant="body1">
                <FormattedMessage {...message} />
              </Typography>
            </li>
          ))}
        </ul>

        <div className={classes.sampleFileContainer}>
          <Typography className={classes.uploadFileInstruction} variant="body1">
            <FormattedMessage {...messages.uploadCsvFile} />
          </Typography>
          <Typography
            className={classes.downloadSampleFileInstruction}
            onClick={handleDownloadSampleFile}
            variant="body1"
          >
            <FormattedMessage {...messages.downloadSampleFile} />
          </Typography>
        </div>

        {renderContent()}
      </div>
    </section>
  );
};

// == Export ======================================================================
export default CustomTerminologies;
